'use client'

/**
 * Submission Recovery (Issue #3227, #3781)
 *
 * Monitors online/offline state and automatically retries
 * pending cached submissions when connectivity is restored.
 *
 * Enhanced in #3781:
 * - Fires on mount (catches post-refresh recovery)
 * - Fires on 'cadence:server-reachable' event from version check
 * - In-flight guard prevents concurrent replay
 * - Shows recovery toast on success
 */

import { useEffect, useCallback, useRef } from 'react'
import { getPendingSubmissions, clearSubmission } from '@/lib/submission-cache'
import { toast } from '@/lib/toast'
import { checkRecoveryFlag } from '@/lib/safe-refresh'

/**
 * Handler function that retries a cached submission.
 * Returns true if the retry succeeded (submission should be cleared).
 * Issue #3789 - submissionId passed for server-side idempotency
 */
export type SubmissionRetryHandler = (
  key: string,
  data: unknown,
  submissionId: string,
) => Promise<boolean>

/**
 * Hook that listens for online events, mount, and server-reachable
 * events, then retries pending submissions.
 *
 * @param retryHandler - Function that knows how to re-submit cached data
 */
export function useSubmissionRecovery(retryHandler: SubmissionRetryHandler) {
  const retryHandlerRef = useRef(retryHandler)
  retryHandlerRef.current = retryHandler

  // In-flight guard to prevent concurrent replay
  const isRetryingRef = useRef(false)

  const retryPendingSubmissions = useCallback(async () => {
    if (isRetryingRef.current) return
    isRetryingRef.current = true

    try {
      const pending = getPendingSubmissions()
      if (pending.length === 0) return

      let recovered = 0

      for (const submission of pending) {
        try {
          const success = await retryHandlerRef.current(
            submission.key,
            submission.data,
            submission.submissionId,
          )
          if (success) {
            clearSubmission(submission.key)
            recovered++
          }
        } catch {
          // Individual retry failed, leave cached for next attempt
        }
      }

      if (recovered > 0) {
        toast.success(
          recovered === 1
            ? 'Your saved changes have been sent.'
            : `${recovered} saved items sent successfully.`,
        )
      }
    } finally {
      isRetryingRef.current = false
    }
  }, [])

  useEffect(() => {
    // Fire on mount - catches post-refresh recovery and initial page load
    checkRecoveryFlag() // Clear the flag if set
    retryPendingSubmissions()

    const handleOnline = () => {
      retryPendingSubmissions()
    }

    const handleServerReachable = () => {
      retryPendingSubmissions()
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('cadence:server-reachable', handleServerReachable)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener(
        'cadence:server-reachable',
        handleServerReachable,
      )
    }
  }, [retryPendingSubmissions])
}
